import type { VisitListResponse } from '@fitting-room/contracts'

type SessionStatus = VisitListResponse['visits'][number]['sessionStatus']
type VisitStatus = VisitListResponse['visits'][number]['status']

/**
 * What the session status means to somebody standing at the rail — not what
 * the database calls it. `open` is the state the console can do nothing about
 * yet; `submitted` is the one it exists to produce, and it is the only badge
 * in the console wearing the brand's gold.
 */
const SESSION_LABEL: Record<SessionStatus, string> = {
  none: 'No link sent',
  open: 'Link open',
  submitted: 'List sent',
  // Past the appointment with no list sent: the link isn't broken, the time has gone.
  expired: 'Appointment passed',
}

export function SessionPill({ status }: { readonly status: SessionStatus }) {
  return (
    <span className={`st-badge st-badge--${status}`} data-session-status={status}>
      {SESSION_LABEL[status]}
    </span>
  )
}

const VISIT_TONE: Record<VisitStatus, string> = {
  booked: 'none',
  completed: 'submitted',
  cancelled: 'alert',
  no_show: 'alert',
}

const VISIT_LABEL: Record<VisitStatus, string> = {
  booked: 'Booked',
  completed: 'Completed',
  cancelled: 'Canceled',
  no_show: 'No show',
}

/**
 * Only drawn when the appointment is not simply booked. Every row carrying a
 * badge that says "normal" is a row where the badges have stopped meaning
 * anything.
 */
export function VisitPill({ status }: { readonly status: VisitStatus }) {
  if (status === 'booked') return null
  return (
    <span className={`st-badge st-badge--${VISIT_TONE[status]}`} data-visit-status={status}>
      {VISIT_LABEL[status]}
    </span>
  )
}

export { SESSION_LABEL }
